import { 
  IconButton, 
  Tooltip, 
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Checkbox,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import { FaFileAudio } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { useSharedData } from '../context/SharedDataContext';

interface DownloadButtonProps {
  originalUrls: string[];
  isOwner: boolean;
  allowDownloads: boolean;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ 
  originalUrls, 
  isOwner, 
  allowDownloads 
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const toast = useToast();
  const { shareData } = useSharedData();

  // Initialize selected indices to all files when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSelectedIndices(new Set(originalUrls.map((_, index) => index)));
    }
  }, [isModalOpen, originalUrls]);

  const decodeFileName = (url: string): string => {
    try {
      // Extract the filename from the URL
      const fileName = url.split('/').pop() || 'audio-file';
      // Decode the URL-encoded characters
      return decodeURIComponent(fileName);
    } catch (error) {
      console.error('Error decoding filename:', error);
      return 'audio-file';
    }
  };

  const getFileNameForDownload = (url: string, index: number): string => {
    // Get the original filename to extract extension
    let originalFileName = decodeFileName(url);
    
    // Remove query parameters from filename (e.g., "file.mp3?rlkey=...&dl=1" -> "file.mp3")
    const queryIndex = originalFileName.indexOf('?');
    if (queryIndex > 0) {
      originalFileName = originalFileName.substring(0, queryIndex);
    }
    
    // Extract file extension from original filename (now without query params)
    const lastDotIndex = originalFileName.lastIndexOf('.');
    const extension = lastDotIndex > 0 && lastDotIndex < originalFileName.length - 1
      ? originalFileName.substring(lastDotIndex)
      : '';
    
    // Try to get custom display name from shareData
    // Display names are keyed by track position ("1", "2", "3")
    const trackPosition = String(index + 1);
    const customName = shareData?.displayNames?.[trackPosition];
    
    if (customName) {
      // Check if custom name already has an extension
      const customNameLastDot = customName.lastIndexOf('.');
      const customNameHasExtension = customNameLastDot > 0 && customNameLastDot < customName.length - 1;
      
      if (customNameHasExtension) {
        // Custom name already has extension, use it as-is
        return customName;
      } else {
        // Use custom name, preserving the original file extension
        return customName + extension;
      }
    }
    
    // Fall back to original filename (without query params)
    return originalFileName;
  };

  const downloadFile = async (url: string, index: number) => {
    let objectUrl: string | null = null;
    let link: HTMLAnchorElement | null = null;

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'force-cache',
        credentials: 'omit',
        headers: {
          'Accept': 'audio/*',
          'Content-Type': 'application/octet-stream'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      
      const blob = await response.blob();
      const fileName = getFileNameForDownload(url, index);
      
      // Create a temporary link and trigger download
      objectUrl = URL.createObjectURL(blob);
      link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);
      link.click();
    } finally {
      // Clean up resources
      if (link) {
        document.body.removeChild(link);
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  };

  // Create and download a zip file containing selected files
  const createZipAndDownload = async (selectedIndicesArray: number[]) => {
    try {
      const zip = new JSZip();
      
      // Create an array of promises for fetching selected files
      const fetchPromises = selectedIndicesArray.map(async (index) => {
        const url = originalUrls[index];
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          cache: 'force-cache',
          credentials: 'omit',
          headers: {
            'Accept': 'audio/*',
            'Content-Type': 'application/octet-stream'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status}`);
        }
        
        const blob = await response.blob();
        const fileName = getFileNameForDownload(url, index);
        
        // Add the file to the zip
        zip.file(fileName, blob);
        
        return { fileName, blob };
      });
      
      // Wait for all fetches to complete
      await Promise.all(fetchPromises);
      
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create a download link for the zip - explicitly set saveAs behavior
      const objectUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'ABmyMix Audio Files.zip';
      link.target = '_blank'; // Add target attribute to encourage saveAs dialog
      link.rel = 'noopener'; // Security best practice with target="_blank"
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Use setTimeout to improve browser compatibility for showing the file picker
      setTimeout(() => {
        link.click();
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        }, 100);
      }, 0);
    } catch (error) {
      console.error('Error creating zip file:', error);
      
      toast({
        title: "Zip download failed",
        description: "Falling back to individual file downloads",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      
      // Fall back to individual downloads
      await Promise.all(selectedIndicesArray.map(index => downloadFile(originalUrls[index], index)));
    }
  };

  const handleDownloadClick = () => {
    if (!originalUrls.length) return;
    setIsModalOpen(true);
  };

  const handleDownloadSelected = async () => {
    const selectedIndicesArray = Array.from(selectedIndices).sort((a, b) => a - b);
    
    if (selectedIndicesArray.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to download",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsModalOpen(false);
    setIsDownloading(true);
    
    try {
      // If only one file selected, always download individually
      // If multiple files, download as zip (desktop) or individually (mobile)
      const shouldZip = selectedIndicesArray.length > 1 && !isMobile;
      
      if (shouldZip) {
        await createZipAndDownload(selectedIndicesArray);
      } else {
        // Download files individually (sequential on mobile, parallel on desktop)
        if (isMobile) {
          // On mobile, download files sequentially
          for (const index of selectedIndicesArray) {
            await downloadFile(originalUrls[index], index);
            // Add a small delay between downloads to ensure browser handles each one
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          // On desktop, download files in parallel
          await Promise.all(selectedIndicesArray.map(index => downloadFile(originalUrls[index], index)));
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "An error occurred while downloading files",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileToggle = (index: number) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIndices(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIndices.size === originalUrls.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(originalUrls.map((_, index) => index)));
    }
  };

  // Debug log the conditions
  console.log('DownloadButton conditions:', {
    isOwner,
    allowDownloads,
    hasUrls: originalUrls.length > 0,
    shouldShow: !isOwner && allowDownloads && originalUrls.length > 0
  });

  // Don't show the button if:
  // 1. User is the owner
  // 2. Downloads are not allowed
  // 3. No original URLs available
  if (isOwner || !allowDownloads || !originalUrls.length) {
    return null;
  }

  return (
    <>
      <Tooltip label="Download files" placement="top">
        <IconButton
          data-testid="download-button"
          aria-label="Download files"
          icon={<FiDownload size={36} />}
          colorScheme="blue"
          variant="ghost"
          onClick={handleDownloadClick}
          isLoading={isDownloading}
          size="lg"
          width="72px"
          height="72px"
          borderRadius="full"
          _hover={{ 
            color: "accent.400",
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{ 
            color: "accent.500",
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
          _focus={{ boxShadow: "none" }}
          _focusVisible={{ boxShadow: "none" }}
        />
      </Tooltip>

      {/* File Selection Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size={{ base: "full", md: "xl" }} isCentered>
        <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
        <ModalContent bg="rgba(20, 20, 30, 0.95)" borderRadius="xl" p={4} mx={4} maxH="90vh" maxW={{ base: "100%", md: "600px" }}>
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              <Icon as={FaFileAudio} color="blue.400" />
              <Text fontSize="xl" fontWeight="bold">
                Select Files to Download
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4} overflowY="auto">
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} justify="flex-end" align="center">
                <Text fontSize="sm" color="gray.300">
                  {selectedIndices.size}/{originalUrls.length} files selected
                </Text>
              </HStack>

              <Divider />

              <Box maxH="400px" overflowY="auto" border="1px solid" borderColor="gray.600" borderRadius="md">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="gray.800" zIndex={1}>
                    <Tr>
                      <Th width="50px">
                        <Checkbox
                          isChecked={selectedIndices.size === originalUrls.length && originalUrls.length > 0}
                          isIndeterminate={selectedIndices.size > 0 && selectedIndices.size < originalUrls.length}
                          onChange={handleSelectAll}
                          colorScheme="blue"
                        />
                      </Th>
                      <Th>File Name</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {originalUrls.map((url, index) => {
                      const fileName = getFileNameForDownload(url, index);
                      return (
                        <Tr key={index} _hover={{ bg: 'gray.700' }}>
                          <Td>
                            <Checkbox
                              isChecked={selectedIndices.has(index)}
                              onChange={() => handleFileToggle(index)}
                              colorScheme="blue"
                            />
                          </Td>
                          <Td>
                            <Text fontSize="sm" noOfLines={1} title={fileName}>
                              {fileName}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter pt={4}>
            <HStack spacing={3} width="100%">
              <Button 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                flex={1}
                size="md"
              >
                Cancel
              </Button>
              <Button 
                colorScheme="blue"
                onClick={handleDownloadSelected}
                isDisabled={selectedIndices.size === 0}
                flex={1}
                size="md"
              >
                Download {selectedIndices.size > 0 ? `(${selectedIndices.size})` : ''}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DownloadButton; 